import { FilterDefs, multiFilter, singleFilter, toggleFilter } from "@homebound/beam";
import { useMemo } from "react";
import { AiProposalStatus, MaterialListingsFilter, MaterialType, MaterialTypeDetail } from "src/generated/graphqlTypes";
import { useMaterialTypes } from "src/hooks/enums/useMaterialTypes";
import {
  MaterialCatalogFilter_MaterialAttributeDimensionFragment,
  MaterialCatalogFilterMetadataQuery,
  useMaterialCatalogFilterDimensionsQuery,
  useMaterialCatalogFilterMetadataQuery,
} from "./useMaterialCatalogFilter.generated";

const staticFilterKeys = ["brand", "item", "type", "tag", "includeDeleted", "reviewingAiImport"] as const;

type StaticFilterKey = (typeof staticFilterKeys)[number];

/**
 * The catalog's local filter shape.
 *
 * Static keys are singular/UI-flavored rather than matching `MaterialListingsFilter`. Per-item dimension
 * filters are extra keys named after the dimension (e.g. `Color`) and get collapsed into `attributeIds`
 * by {@link mapToFilter}.
 */
export type MaterialCatalogFilter = {
  brand?: string[];
  item?: string;
  type?: MaterialType[];
  tag?: string[];
  includeDeleted?: boolean;
  /** Only listings whose AI import has landed and is waiting on review. */
  reviewingAiImport?: boolean;
  [dimensionName: string]: string | string[] | MaterialType[] | boolean | undefined;
};

/**
 * Builds the catalog's `FilterDefs` for `GridTableLayout`'s collapsible filter panel.
 *
 * The dimension filters depend on the currently selected item, so `selectedItemId` has to be threaded
 * back in from the filter state that these defs produce.
 */
export function useMaterialCatalogFilterDefs(selectedItemId: string | undefined): FilterDefs<MaterialCatalogFilter> {
  const { data } = useMaterialCatalogFilterMetadataQuery({ fetchPolicy: "cache-first" });
  const types = useMaterialTypes();
  // Only the selected item's dimensions are needed, so skip this until the user picks one instead of
  // pulling every item's dimensions/values along with the item list.
  const { data: dimensionsData } = useMaterialCatalogFilterDimensionsQuery({
    variables: { itemId: selectedItemId! },
    skip: !selectedItemId,
    fetchPolicy: "cache-first",
  });
  const dimensions = dimensionsData?.materialAttributeDimensions;
  return useMemo(() => createFilterDefs(data, types, dimensions ?? []), [data, types, dimensions]);
}

function createFilterDefs(
  data: MaterialCatalogFilterMetadataQuery | undefined,
  types: MaterialTypeDetail[],
  dimensions: MaterialCatalogFilter_MaterialAttributeDimensionFragment[],
): FilterDefs<MaterialCatalogFilter> {
  const type = multiFilter({
    label: "Material Type",
    options: types,
    getOptionValue: (o) => o.code,
    getOptionLabel: (o) => o.name,
  });

  const item = singleFilter({
    label: "Item",
    options: data?.items ?? [],
    getOptionValue: (o) => o.id,
    getOptionLabel: (o) => o.name,
  });

  const brand = multiFilter({
    label: "Brand",
    options: data?.materialBrands ?? [],
    getOptionValue: (o) => o.id,
    getOptionLabel: (o) => o.name,
  });

  const tag = multiFilter({
    label: "Tag",
    options: data?.materialVariantTags ?? [],
    getOptionValue: (o) => o.id,
    getOptionLabel: (o) => o.name,
  });

  const includeDeleted = toggleFilter({ label: "Show Archived" });
  const reviewingAiImport = toggleFilter({ label: "Show Pending AI Review" });

  const dimensionFilters = dimensions.keyBy(
    ({ name }) => name,
    ({ name, values }) =>
      multiFilter({
        label: name,
        options: values ?? [],
        getOptionValue: (o) => o.id,
        // NOTE: This field might need to be updated to handle range values
        //    SC-55230
        getOptionLabel: (o) => o?.textValue ?? "",
      }),
  );

  return { type, item, brand, tag, ...dimensionFilters, includeDeleted, reviewingAiImport };
}

/** Maps the local filter state (plus the search box) into the `materialListings` filter. */
export function mapToFilter(filter: MaterialCatalogFilter, searchString?: string): MaterialListingsFilter {
  const { item, type, brand, tag, includeDeleted, reviewingAiImport } = filter;
  const attributeIds = Object.entries(filter)
    .filter(([key]) => !isStaticFilterKey(key))
    .flatMap(([, value]) => (Array.isArray(value) ? value : []));
  return {
    itemId: item ? [item] : undefined,
    type,
    brandId: brand,
    tags: tag,
    ...(attributeIds.nonEmpty ? { attributeIds } : {}),
    includeDeleted,
    ...(reviewingAiImport && { aiProposalStatus: [AiProposalStatus.Reviewing] }),
    ...(searchString && { search: searchString }),
  };
}

function isStaticFilterKey(key: string): key is StaticFilterKey {
  return staticFilterKeys.includes(key as StaticFilterKey);
}
